
import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Send, Loader2, BookOpen, Lightbulb, Target, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useWritingEvaluation } from "@/hooks/useWritingEvaluation";
import { WritingSession } from "@/types/writing";
import WritingSuggestionsPopover from "./WritingSuggestionsPopover";
import WritingContentValidator from "./WritingContentValidator";

interface WritingInterfaceProps {
  topic: string;
  grade: string;
  subject: string;
  writingType: string;
  onSubmit: (session: WritingSession) => void;
  onBack: () => void;
}

const WritingInterface = ({ topic, grade, subject, writingType, onSubmit, onBack }: WritingInterfaceProps) => {
  const [writingText, setWritingText] = useState("");
  const [isContentValid, setIsContentValid] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { evaluateWriting, isLoading } = useWritingEvaluation();

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSubmit = async () => {
    if (!writingText.trim() || !isContentValid) return;

    try {
      const result = await evaluateWriting({
        text: writingText,
        topic,
        grade,
        subject,
        writingType
      });

      const session: WritingSession = {
        id: crypto.randomUUID(),
        originalText: writingText,
        enhancedText: result.enhancedText,
        feedback: result.feedback,
        grade,
        subject,
        topic,
        writingType,
        badges: result.badges || [],
        createdAt: new Date().toISOString()
      };

      onSubmit(session);
    } catch (error) {
      console.error('Writing evaluation failed:', error);
    }
  };

  const wordCount = writingText.trim().split(/\s+/).filter(word => word.length > 0).length;
  const minWords = parseInt(grade) <= 3 ? 25 : 50;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="max-w-4xl mx-auto"
    >
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Topics
            </Button>
            <div className="flex gap-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                <BookOpen className="w-4 h-4 mr-1" />
                Grade {grade}
              </Badge>
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                {writingType}
              </Badge>
            </div>
          </div>
          
          <div className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold text-blue-700 flex items-center justify-center gap-2">
              <Target className="h-6 w-6" />
              Write About: {topic}
            </CardTitle>
            <div className="flex justify-center">
              <WritingSuggestionsPopover 
                topic={topic}
                grade={grade}
                writingType={writingType}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-blue-700 font-medium text-lg">Your Writing:</label>
              <div className="flex items-center gap-4 text-sm">
                <span className={`${wordCount >= minWords ? 'text-green-600' : 'text-gray-500'}`}>
                  Words: {wordCount}/{minWords} minimum
                </span>
              </div>
            </div>
            
            <Textarea
              ref={textareaRef}
              placeholder={`Start writing about "${topic}" here. Remember to include details and use your best writing skills!`}
              value={writingText}
              onChange={(e) => setWritingText(e.target.value)}
              className="min-h-96 text-lg leading-relaxed border-2 border-blue-200 focus:border-blue-400"
              style={{ fontFamily: 'Georgia, serif' }}
            />
            
            <WritingContentValidator 
              content={writingText}
              onValidationChange={setIsContentValid}
            />
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleSubmit}
              disabled={!writingText.trim() || wordCount < minWords || isLoading || !isContentValid}
              className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-bold py-3 px-8 rounded-xl text-lg disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Getting Feedback...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Get My Feedback!
                </>
              )}
            </Button>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-yellow-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 font-medium text-base">
                  <strong>Writing Tips:</strong>
                </p>
                <ul className="text-yellow-700 text-sm mt-2 space-y-1">
                  <li>• Make sure your writing is appropriate for school</li>
                  <li>• Include lots of details to make your writing interesting</li>
                  <li>• Check your spelling and grammar before submitting</li>
                  <li>• Write at least {minWords} words to get the best feedback</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WritingInterface;
